import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { Constants } from "../../../constants/Constants";
import type { FormBuilderForm } from "../../../generated/nuggetKmsApi/graphql";
import {
	FilterOperators,
	useGetFormBuilderFormLazyQuery,
	useGetSubmissionsLazyQuery,
} from "../../../generated/nuggetKmsApi/graphql";
import type { SubmissionRow } from "./formBuilderExportUtils";
import {
	buildCsvFromSubmissions,
	buildExportColumnsFromForm,
	EXPORT_CHUNK_SIZE,
	triggerCsvDownload,
} from "./formBuilderExportUtils";

export const useExportFormResponses = (formId: number, form?: FormBuilderForm | null) => {
	const [loading, setLoading] = useState(false);

	const [getForm] = useGetFormBuilderFormLazyQuery({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const [getSubmissions] = useGetSubmissionsLazyQuery({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const exportResponses = useCallback(async () => {
		setLoading(true);
		try {
			let formData = form;
			if (!formData) {
				const result = await getForm({ variables: { id: formId } });
				formData = result.data?.getFormBuilderForm ?? null;
			}

			if (!formData?.sections?.length) {
				toast.error("Form has no questions to export.");
				return;
			}

			const columns = buildExportColumnsFromForm(formData);
			const allRows: SubmissionRow[] = [];
			let offset = 0;
			let totalCount = 0;

			do {
				const result = await getSubmissions({
					variables: {
						input: {
							filter: {
								fields: { formId: { val: formId, op: FilterOperators.Equal } },
							},
							pagination: { limit: EXPORT_CHUNK_SIZE, offset },
						},
					},
				});

				const chunk = result.data?.getSubmissions?.rows ?? [];
				totalCount = result.data?.getSubmissions?.count ?? 0;
				allRows.push(...chunk);
				offset += EXPORT_CHUNK_SIZE;
			} while (allRows.length < totalCount && offset < totalCount);

			const csv = buildCsvFromSubmissions(columns, allRows);
			triggerCsvDownload(csv, `form-${formId}-responses.csv`);

			toast.success(`Exported ${allRows.length} submission(s).`);
		} catch (error: unknown) {
			toast.error((error as Error)?.message ?? "Failed to export responses.");
		} finally {
			setLoading(false);
		}
	}, [formId, form, getForm, getSubmissions]);

	return { exportResponses, loading };
};
